import type { Account } from '../types/account'
import type { Cluster } from '../types/cluster'

export type ProjectDistributionListOption = {
	label: string
	value: string
	description?: string
	disabled?: boolean
}

export function resolveDistributionListValue(value: unknown): string {
	if (value != null && typeof value === 'object' && 'value' in value) {
		return String((value as { value: unknown }).value ?? '').trim()
	}
	return String(value ?? '').trim()
}

export function normalizeAccountWorkloadType(value: unknown): 'EKS' | 'Non-EKS' {
	const normalized = String(value ?? '')
		.trim()
		.toLowerCase()
		.replace(/[_\s]+/g, '-')
	if (normalized === 'eks') return 'EKS'
	if (normalized === 'non-eks') return 'Non-EKS'
	if (normalized.includes('non') && normalized.includes('eks')) return 'Non-EKS'
	if (normalized.includes('eks')) return 'EKS'
	return 'Non-EKS'
}

export function accountHasApprovedCluster(
	clusters: Cluster[],
	distributionList: string,
	environment?: string
): boolean {
	const dl = resolveDistributionListValue(distributionList).toLowerCase()
	const env = String(environment ?? '').trim().toLowerCase()
	return clusters.some((c) => {
		if (c.status !== 'APPROVED') return false
		if (
			resolveDistributionListValue(c.details?.distributionList).toLowerCase() !==
			dl
		) {
			return false
		}
		if (env) {
			return String(c.details?.environment ?? '').trim().toLowerCase() === env
		}
		return true
	})
}

const CLUSTER_REQUIRED_MESSAGE =
	'Cluster onboarding must be completed for this account before project onboarding'

export function buildProjectDistributionListOptions(params: {
	accounts: Account[]
	clusters: Cluster[]
	environment?: string
}): ProjectDistributionListOption[] {
	const { accounts, clusters, environment } = params
	const envScalar =
		environment != null && typeof environment === 'object' && 'value' in environment
			? String((environment as { value: unknown }).value ?? '').trim()
			: String(environment ?? '').trim()

	const approved = (accounts ?? []).filter(
		(a) => a.status === 'APPROVED' && a.details?.distributionList
	)

	const seen = new Set<string>()
	return approved
		.map((a) => ({
			dl: String(a.details.distributionList).trim(),
			workloadType: normalizeAccountWorkloadType(a.details?.accountWorkloadType)
		}))
		.filter(({ dl }) => {
			const key = dl.toLowerCase()
			if (!dl || seen.has(key)) return false
			seen.add(key)
			return true
		})
		.sort((a, b) => a.dl.localeCompare(b.dl))
		.map(({ dl, workloadType }) => {
			if (workloadType === 'Non-EKS') {
				return { label: dl, value: dl }
			}
			const hasCluster = accountHasApprovedCluster(clusters, dl, envScalar)
			return {
				label: dl,
				value: dl,
				disabled: !hasCluster,
				description: hasCluster ? undefined : CLUSTER_REQUIRED_MESSAGE
			}
		})
}
